"use client";

import React, { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from 'next/link';
import { motion, AnimatePresence } from "framer-motion";
// import Turnstile from "react-turnstile"; // کامنت شد
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { toast } from "react-hot-toast";

// Local Imports
import { useAuth } from '@/context/AuthContext';
import { UserPlus } from "lucide-react";
import { RegistrationDetailsStep } from "./RegistrationDetailsStep";
import { VerificationStep } from "./VerificationStep";
import { GoogleSpinner } from "./ui";
import { formSchema, FormData, FormStep } from "./schemas";

// =================================================================
// کامپوننت اصلی صفحه
// =================================================================
export default function RegisterPage() {
    const { startRegistration, verifyRegistration, isLoading: isAuthLoading } = useAuth();
    const [step, setStep] = useState<FormStep>('details');

    const [isStartingReg, setIsStartingReg] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);

    const [timer, setTimer] = useState(120);
    const [isTimerActive, setIsTimerActive] = useState(false);

    // const turnstileRef = useRef<TurnstileInstance>(null); // کامنت شد
    // const resendTurnstileRef = useRef<TurnstileInstance>(null); // کامنت شد

    const methods = useForm<FormData>({ resolver: zodResolver(formSchema), defaultValues: { identifier: "", password: "", otp_code: "" }});

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (isTimerActive && timer > 0) {
            interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
        } else if (timer === 0) {
            setIsTimerActive(false);
        }
        return () => clearInterval(interval);
    }, [isTimerActive, timer]);

    const startTimer = () => {
        setTimer(120);
        setIsTimerActive(true);
    };

    const handleStartRegistration = async (data: FormData, turnstileToken: string) => {
        console.log("handleStartRegistration called with data:", data, "and turnstileToken:", turnstileToken);
        setIsStartingReg(true);
        // توکن کپچا اکنون یک مقدار موقت است
        const success = await startRegistration({ ...data, 'captcha': turnstileToken });
        if (success) {
            setStep('verify');
            startTimer();
        }
        setIsStartingReg(false);
    };

    const handleResendCode = async (token: string) => {
        console.log("handleResendCode called with identifier:", methods.getValues("identifier"), "and token:", token);
        setIsResending(true);
        const data = methods.getValues();
        // توکن کپچا اکنون یک مقدار موقت است
        const success = await startRegistration({ ...data, 'captcha': token });
        if (success) {
            toast.success("کد جدید با موفقیت ارسال شد.");
            startTimer();
        }
        setIsResending(false);
    }

    const handleVerifyRegistration = async (data: FormData) => {
        console.log("handleVerifyRegistration called with data:", data);
        const registrationData = { username: methods.getValues("identifier"), otp_code: data.otp_code };
        await verifyRegistration(registrationData);
    };

    const pageText = step === 'details'
        ? "برای پیوستن به ما، اطلاعات خود را وارد کنید."
        : "فقط یک مرحله تا تکمیل ثبت‌نام باقی مانده است.";

    return (
        <FormProvider {...methods}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }} className="relative w-full max-w-md overflow-hidden lg:min-w-sm rounded-2xl shadow-2xl bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm border border-zinc-200/50 dark:border-zinc-700/50">

                <AnimatePresence>
                    {isGoogleLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-zinc-800/50 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4"
                        >
                            <GoogleSpinner />
                            <p className="text-white font-semibold text-lg">ایجاد حساب با گوگل...</p>
                            <p className="text-zinc-300 text-sm">لطفاً منتظر بمانید</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex flex-col items-center text-center p-8 bg-zinc-100/50 dark:bg-zinc-800/50">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}>
                        <div className="mx-auto h-16 w-16 bg-blue-100/50 dark:bg-blue-900/50 rounded-full flex items-center justify-center border border-blue-200/50 dark:border-blue-700/50">
                            <UserPlus className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                    </motion.div>
                    <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 mt-4">ایجاد حساب جدید</h1>
                    <div className="h-6 mt-1">
                        <AnimatePresence mode="wait">
                            <motion.p
                                key={pageText}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.2 }}
                                className="text-sm text-zinc-500 dark:text-zinc-400"
                            >
                                {pageText}
                            </motion.p>
                        </AnimatePresence>
                    </div>
                </div>
                <div className="p-8">
                    <AnimatePresence mode="wait">
                        {step === 'details' ? (
                            <RegistrationDetailsStep
                                onFormSubmit={handleStartRegistration}
                                isLoading={isStartingReg}
                                isGoogleLoading={isGoogleLoading}
                                setIsGoogleLoading={setIsGoogleLoading}
                            />
                        ) : (
                            <VerificationStep
                                onFormSubmit={handleVerifyRegistration}
                                onResend={handleResendCode} // ✅ onResend به کامپوننت پاس داده می‌شود
                                isLoading={isAuthLoading}
                                isResending={isResending}
                                timer={timer}
                                isTimerActive={isTimerActive}
                                // turnstileRef={resendTurnstileRef} // کامنت شد
                            />
                        )}
                    </AnimatePresence>
                    <p className="text-sm text-center text-zinc-500 dark:text-zinc-400 mt-8">
                        قبلاً ثبت‌نام کرده‌اید؟ <Link href="/login" className="font-semibold text-blue-600 hover:underline">وارد شوید</Link>
                    </p>
                </div>
            </motion.div>
            {/* Hidden Turnstile for resending OTP in register page - کامنت شد
            <div className="opacity-0 h-0 overflow-hidden">
                <Turnstile
                    ref={resendTurnstileRef}
                    sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                    onVerify={handleResendCode}
                    action="register_resend"
                />
            </div>
            */}
        </FormProvider>
    );
}
